import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Tab, Tabs, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useQuery } from '@tanstack/react-query';
import { api, apiGet } from '../../../api/client';
import { PageHeader } from '../../../components/common/PageHeader';
import { PermissionGate } from '../../../components/auth/PermissionGate';
import { SelectField } from '../../../components/fields/SelectField';
import { ReportKey, REPORT_TABS, defaultRange, linkedNote, linkedSummary, reportBlocker, reportFileName, reportParams, rupees, summaryRowNote } from './reportLogic';

// Branch Cash reports (read-only). Every number is computed on the server from the cash ledger; this page only filters and shows
// them. The CSV button downloads the server's own export, which passes the same permission and branch checks as the screen.

const unwrap = (d: any) => d?.data ?? d;
const errMsg = (e: any, fallback: string) => e?.response?.data?.error ?? fallback;
const todayInIndia = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const PAGE_SIZE = 100;
const TYPES = ['SALES_CASH_IN', 'SALES_CASH_REVERSAL', 'ADVANCE_CASH_IN', 'ADVANCE_CASH_REVERSAL', 'EXPENSE_PAYMENT', 'EXPENSE_REVERSAL', 'RECEIPT_IN', 'RECEIPT_REVERSAL', 'TRANSFER_DISPATCH', 'TRANSFER_RECEIPT', 'ADJUSTMENT_IN', 'ADJUSTMENT_OUT', 'SUPPLIER_PAYMENT', 'SUPPLIER_PAYMENT_REVERSAL'];
const num = { align: 'right' as const, sx: { fontFamily: 'monospace', whiteSpace: 'nowrap' as const } };
const when = (v: string | null | undefined) => (v ? new Date(v).toLocaleString('en-GB', { timeZone: 'Asia/Kolkata' }) : '-');

const SUMMARY_COLS: Array<[string, string]> = [
  ['salesCash', 'Sales cash'], ['advanceCash', 'Advance cash'], ['manualReceipts', 'Manual receipts'], ['expensePayments', 'Expense payments'],
  ['transfersOut', 'Transfers out'], ['transfersIn', 'Transfers in'], ['adjustmentsIn', 'Adj. in'], ['adjustmentsOut', 'Adj. out'],
  ['reversalsOut', 'Reversals / cash returns'], ['reversalsIn', 'Reversals in'],
];

function SummaryTable({ data }: { data: any }) {
  const c = data.consolidated;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell><TableCell>Branch</TableCell><TableCell align="right">Opening</TableCell>
            {SUMMARY_COLS.map(([, h]) => <TableCell key={h} align="right">{h}</TableCell>)}
            <TableCell align="right">Expected closing</TableCell><TableCell align="right">Actual count</TableCell><TableCell align="right">Variance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((r: any) => (
            <TableRow key={r.dayId} hover>
              <TableCell>{r.businessDate}</TableCell>
              <TableCell>{r.branchCode}{r.openingUnverified && <Chip size="small" color="warning" label="opening unverified" title={summaryRowNote(r)} sx={{ ml: 1 }} />}</TableCell>
              <TableCell {...num}>{rupees(r.opening)}</TableCell>
              {SUMMARY_COLS.map(([k]) => <TableCell key={k} {...num}>{rupees(r[k])}</TableCell>)}
              <TableCell {...num}>{rupees(r.expectedClosing)}</TableCell>
              <TableCell {...num}>{r.actualCount === null ? '-' : rupees(r.actualCount)}</TableCell>
              <TableCell {...num}>{r.variance === null ? '-' : rupees(r.variance)}</TableCell>
            </TableRow>
          ))}
          {data.rows.length === 0 && <TableRow><TableCell colSpan={SUMMARY_COLS.length + 6} align="center" sx={{ color: 'text.secondary', py: 2 }}>No business days in this range</TableCell></TableRow>}
          {data.rows.length > 0 && (
            <TableRow sx={{ '& td': { fontWeight: 700 } }}>
              <TableCell colSpan={2}>All permitted branches ({c.branchCount})</TableCell>
              <TableCell {...num}>{rupees(c.openingVerifiedTotal)}</TableCell>
              {SUMMARY_COLS.map(([k]) => <TableCell key={k} {...num}>{rupees(c[k])}</TableCell>)}
              <TableCell {...num}>{rupees(c.expectedClosing)}</TableCell><TableCell {...num}>-</TableCell><TableCell {...num}>-</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {c.unverifiedOpeningDays > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {c.unverifiedOpeningDays} day(s) have an unverified opening count, so their expected closing is not shown and the combined expected closing is withheld.
        </Alert>
      )}
    </Box>
  );
}

function MovementsTable({ data }: { data: any }) {
  return (
    <>
      <Typography variant="body2" sx={{ mb: 1 }}>Totals for the filter: in {rupees(data.totals.totalIn)}, out {rupees(data.totals.totalOut)}, net {rupees(data.totals.net)}</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Date</TableCell><TableCell>Branch</TableCell><TableCell>Type</TableCell><TableCell align="right">Amount</TableCell>
            <TableCell>Source</TableCell><TableCell>Recorded</TableCell><TableCell>Late</TableCell><TableCell>Reason</TableCell><TableCell>Linked to</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.rows.map((m: any) => (
              <TableRow key={m.id} hover>
                <TableCell>{m.businessDate}</TableCell><TableCell>{m.branchCode}</TableCell><TableCell>{m.movementType}</TableCell>
                <TableCell {...num}>{m.direction === 'OUT' ? '-' : ''}{rupees(m.amount)}</TableCell>
                <TableCell>{m.sourceRef ?? m.sourceType}</TableCell><TableCell>{when(m.occurredAt)}</TableCell>
                <TableCell>{m.isLate ? <Chip size="small" color="warning" label="late" /> : ''}</TableCell><TableCell>{m.reason ?? ''}</TableCell>
                <TableCell>{linkedNote(m)}</TableCell>
              </TableRow>
            ))}
            {data.rows.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 2 }}>No movements match</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Box>
    </>
  );
}

function ReconciliationView({ data }: { data: any }) {
  const rows: Array<[string, string]> = [
    ['Sales cash received (source records)', rupees(data.source.in)], ['  of which reversed at source', rupees(data.source.reversed)],
    ['Ledger cash in', rupees(data.ledger.in)], ['Ledger cash out', rupees(data.ledger.out)], ['Ledger net', rupees(data.ledger.net)],
    ['Not yet imported', `${data.pending.receipts} receipt(s), ${rupees(data.pending.receiptsAmount)}`], ['Reversals not yet flagged', String(data.pending.reversalsToFlag)],
    ['Reversals awaiting a decision', `${data.reviews.awaiting}, ${rupees(data.reviews.awaitingAmount)}`],
    ['Decided: cash returned', rupees(data.reviews.cashReturnedAmount)], ['Decided: book correction only', rupees(data.reviews.bookOnlyAmount)],
    ['Reversals from before cash tracking', String(data.warnings.orphanReversals)], ['Cash payments with no branch', String(data.warnings.unattributedCashPayments)],
    ['Advances with refunds (not counted)', String(data.warnings.advancesWithRefunds)],
  ];
  return (
    <>
      <Alert severity={data.inSync ? 'success' : 'warning'} sx={{ mb: 1 }}>{data.inSync ? 'Every cash receipt is in the ledger.' : 'Some sales cash is not in the ledger yet - import it from the Branch Cash screen.'}</Alert>
      <Table size="small"><TableBody>{rows.map(([k, v]) => <TableRow key={k}><TableCell>{k}</TableCell><TableCell {...num}>{v}</TableCell></TableRow>)}</TableBody></Table>
    </>
  );
}

function ReviewsTable({ data }: { data: any }) {
  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>{data.byStatus.map((s: any) => <Chip key={s.status} label={`${s.status}: ${s.count} (${rupees(s.amount)})`} />)}</Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Branch</TableCell><TableCell>Source</TableCell><TableCell align="right">Amount</TableCell><TableCell>Reversed</TableCell><TableCell>Why</TableCell>
            <TableCell>Status</TableCell><TableCell>Cash effect</TableCell><TableCell>Decision</TableCell><TableCell>Evidence</TableCell><TableCell>Original cash entry</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.rows.map((r: any) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.branchCode}</TableCell><TableCell>{r.sourceRef ?? r.sourceType}</TableCell><TableCell {...num}>{rupees(r.amount)}</TableCell>
                <TableCell>{when(r.sourceReversedAt)}</TableCell><TableCell>{r.sourceReversalReason ?? ''}</TableCell><TableCell>{r.status}</TableCell>
                <TableCell>{r.cashEffect === 'CASH_OUT' ? 'Cash out' : 'None'}</TableCell><TableCell>{r.decisionReason ?? ''}</TableCell><TableCell>{r.evidenceRef ?? ''}</TableCell>
                <TableCell>{r.originalMovement ? linkedSummary(r.originalMovement) : ''}</TableCell>
              </TableRow>
            ))}
            {data.rows.length === 0 && <TableRow><TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 2 }}>No reversal reviews</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Box>
    </>
  );
}

function ReportsBody() {
  const today = todayInIndia();
  const initial = defaultRange(today, 7);
  const [tab, setTab] = useState<ReportKey>('daily-summary');
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [branchId, setBranchId] = useState('');
  const [type, setType] = useState(''); const [direction, setDirection] = useState(''); const [late, setLate] = useState(''); const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [branches, setBranches] = useState<Array<{ id: string; code: string; name: string }>>([]);
  const [downloadError, setDownloadError] = useState('');

  const filters = { from, to, branchId, type, direction, late, status };
  const blocker = reportBlocker(tab, filters);
  const paged = tab === 'movements' || tab === 'reversal-reviews';
  const pagingParams: Record<string, number> = paged ? { limit: PAGE_SIZE, offset: page * PAGE_SIZE } : {};
  useEffect(() => { setPage(0); }, [tab, from, to, branchId, type, direction, late, status]);

  const q = useQuery({
    queryKey: ['bc-report', tab, filters, page],
    enabled: !blocker,
    queryFn: async () => unwrap(await apiGet(`/branch-cash/reports/${tab}`, reportParams(tab, filters, pagingParams))) as any,
  });
  // The branch picker is fed by the report's own scope (the branches this user may see) - it needs no other permission.
  useEffect(() => { if (q.data && !branchId && q.data.branches?.length) setBranches(q.data.branches); }, [q.data, branchId]);

  const download = async () => {
    setDownloadError('');
    try {
      const res = await api.get(`/branch-cash/reports/${tab}`, { params: { ...reportParams(tab, filters), format: 'csv' }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = reportFileName(tab, filters);
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e: any) {
      setDownloadError(errMsg(e, 'Could not download the CSV'));
    }
  };

  const data = q.data;
  const total = data?.total ?? 0;
  return (
    <>
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" useFlexGap>
          {REPORT_TABS.find((t) => t.key === tab)!.needsRange && (
            <>
              <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </>
          )}
          <Box sx={{ minWidth: 220 }}>
            <SelectField size="small" label="Branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}
              emptyLabel={tab === 'sales-cash-reconciliation' ? 'Choose a branch' : 'All permitted branches'}
              options={branches.map((b) => ({ value: b.id, label: `${b.code} - ${b.name}` }))} />
          </Box>
          {tab === 'movements' && (
            <>
              <Box sx={{ minWidth: 190 }}><SelectField size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)} emptyLabel="All types" options={TYPES.map((t) => ({ value: t, label: t }))} /></Box>
              <Box sx={{ minWidth: 120 }}><SelectField size="small" label="Direction" value={direction} onChange={(e) => setDirection(e.target.value)} emptyLabel="Both" options={[{ value: 'IN', label: 'In' }, { value: 'OUT', label: 'Out' }]} /></Box>
              <Box sx={{ minWidth: 120 }}><SelectField size="small" label="Late" value={late} onChange={(e) => setLate(e.target.value)} emptyLabel="All" options={[{ value: 'true', label: 'Late only' }, { value: 'false', label: 'Not late' }]} /></Box>
            </>
          )}
          {tab === 'reversal-reviews' && (
            <Box sx={{ minWidth: 170 }}><SelectField size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} emptyLabel="All"
              options={[{ value: 'PENDING', label: 'Pending' }, { value: 'CASH_RETURNED', label: 'Cash returned' }, { value: 'BOOK_ONLY', label: 'Book only' }]} /></Box>
          )}
          <PermissionGate permission="cash:report_export">
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!!blocker || q.isFetching} onClick={download}>Download CSV</Button>
          </PermissionGate>
        </Stack>
      </Paper>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }} variant="scrollable">
        {REPORT_TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>
      {blocker && <Alert severity="info" sx={{ mb: 1 }}>{blocker}</Alert>}
      {downloadError && <Alert severity="error" sx={{ mb: 1 }}>{downloadError}</Alert>}
      {q.isError && <Alert severity="error" sx={{ mb: 1 }}>{errMsg(q.error, 'Could not load the report')}</Alert>}
      {data && !blocker && (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Generated {when(data.generatedAt)} - business dates in {data.timezone === 'MIXED' ? 'each branch\'s own timezone' : data.timezone}
            {data.businessDateRange ? ` - ${data.businessDateRange.from} to ${data.businessDateRange.to}` : ''}
          </Typography>
          {tab === 'daily-summary' && <SummaryTable data={data} />}
          {tab === 'movements' && <MovementsTable data={data} />}
          {tab === 'sales-cash-reconciliation' && <ReconciliationView data={data} />}
          {tab === 'reversal-reviews' && <ReviewsTable data={data} />}
          {paged && <TablePagination component="div" count={total} page={page} rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]} onPageChange={(_, p) => setPage(p)} />}
        </Paper>
      )}
    </>
  );
}

export function BranchCashReportsPage() {
  return (
    <Box>
      <PageHeader title="Branch Cash Reports" subtitle="Read-only. Cash figures come from the cash ledger; UPI, card and bank payments are never counted as cash." />
      <PermissionGate permission="cash:report_view" fallback={<Alert severity="warning">You do not have permission to view Branch Cash reports.</Alert>}>
        <ReportsBody />
      </PermissionGate>
    </Box>
  );
}

export default BranchCashReportsPage;
